'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const {
    Component
} = React;
const Relay = require('react-relay');
const get = require('lodash.get');

// Stores
const ItemStore = require('../../stores/ItemStore.es');
const UserStore = require('../../../global/stores/UserStore.es');

// Components
const ModalContainer = require('dibs-elements/dist/ModalContainer').default;
const ShippingDetailsModal = require('./ShippingDetailsModal');
require('react-modal-wrapper/dist/main.css');

const { OPEN_DETAILS_FROM_SIDEBAR, OPEN_DETAILS_FROM_DESCRIPTION } = require('../../constants/shippingConstants');

// Helpers
const provideContext = require('fluxible-addons-react/provideContext');
const connectToStores = require('fluxible-addons-react/connectToStores');
const { getValidQuote } = require('./shippingHelpers');
const { trackGaEvent } = require('../../helpers/pdpTracking');
const chatUtils = require('../../../global/utils/social/chat');
const { getEnvironment } = require('../../../global/relayEnvironmnentProvider.es');

// Request Shipping Quote Modal
const { connect } = require('react-redux');
const { default: RequestShippingModal } = require('dibs-request-shipping-modal');
const showAuthModal = require('../../../contact/shared/helpers/relayAuthHelper');
require('dibs-request-shipping-modal/dist/styles.css');

function onOpen({ output, source, customQuoteAvailable }) {
    return () => {
        const labels = [`pdp ${source}`];
        labels.push(output ? 'shipping quote provided' : 'no shipping quote provided');
        if (customQuoteAvailable) {
            labels.push('custom quote request available');
        }
        trackGaEvent('product', 'view shipping details', labels.join('|'));
        chatUtils.track.shippingDetailsClick();
    };
}

function onShowQuoteRequest(source) {
    return () => {
        trackGaEvent('product', 'request shipping quote clicked', `pdp ${source}`);
    };
}

function hasNoQuotes({domestic, local, shipmentQuotes} = {}) {
    return !domestic.length && !local.length && !shipmentQuotes.length;
}

class ShippingDetailsLink extends Component {
    constructor(props) {
        super(props);
        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        // isSubmitAfterAuth and openModalKey are from RequestShippingModal's redux store
        this.state = { isOpened: props.isSubmitAfterAuth && props.openModalKey
            && props.openModalKey === props.pagePosition };
    }
    openModal() {
        this.setState({ isOpened: true });
        this.props.relay.forceFetch();
    }
    closeModal() {
        this.setState({ isOpened: false });
    }
    render() {
        const { currency, quotes, user = {}, link, source, itemId, userId, dealerPk, viewer,
            customQuoteAvailable, dealerData, trackingLocation,
            pagePosition, item } = this.props;
        const { output } = getValidQuote({
            quotes: quotes.domestic,
            regionType: 'DOMESTIC',
            currency
        });
        let detailsModal = (
            <ShippingDetailsModal
                quotes={quotes}
                currency={currency}
                user={user}
                source={source}
                itemId={itemId}
                userId={userId}
                closeShippingModal={this.closeModal}
                viewer={viewer}
                item={item}
            />
        );
        if (get(user, 'canRequestShippingQuote')) {
            if (hasNoQuotes(quotes)) {
                detailsModal = null;
            }
            return (
                <div className="pdp-shipping-details-link">
                    { React.cloneElement(link, {onClick: this.openModal}) }
                    <RequestShippingModal
                        environment={getEnvironment()}
                        closeOnEsc
                        closeOnOutsideClick
                        closeShippingModal={this.closeModal}
                        isOpened={this.state.isOpened}
                        onOpen={(onOpen({output, source, customQuoteAvailable}))}
                        onShowQuoteRequest={onShowQuoteRequest(source)}
                        itemId={itemId}
                        userId={userId}
                        user={user}
                        dealerPk={dealerPk}
                        viewer={viewer}
                        dealerData={dealerData}
                        showAuthModal={showAuthModal}
                        trackingLocation={trackingLocation}
                        modalKey={pagePosition}
                        conversation={null}
                        item={null}
                    >
                        { detailsModal }
                    </RequestShippingModal>
                </div>
            );
        }
        return (
            <div className="pdp-shipping-details-link">
                { React.cloneElement(link, {onClick: this.openModal}) }
                <ModalContainer
                    modalSize="medium"
                    onClose={this.closeModal}
                    isOpen={this.state.isOpened}
                    onOpen={(onOpen({output, source, customQuoteAvailable}))}
                >
                    { detailsModal }
                </ModalContainer>
            </div>
        );
    }
}

ShippingDetailsLink.propTypes = {
    context: PropTypes.object,
    customQuoteAvailable: PropTypes.bool,
    currency: PropTypes.string.isRequired,
    quotes: PropTypes.object.isRequired,
    user: PropTypes.object,
    itemId: PropTypes.string,
    userId: PropTypes.string,
    dealerPk: PropTypes.string,
    viewer: PropTypes.object,
    link: PropTypes.element.isRequired,
    source: PropTypes.oneOf([OPEN_DETAILS_FROM_SIDEBAR, OPEN_DETAILS_FROM_DESCRIPTION]).isRequired,
    regionType: PropTypes.string.isRequired,
    favoriteDealer: PropTypes.object,
    favoriteDealerCta: PropTypes.object,
    dealerData: PropTypes.object,
    relay: PropTypes.object,
    trackingLocation: PropTypes.string,
    isSubmitAfterAuth: PropTypes.bool,
    openModalKey: PropTypes.string,
    pagePosition: PropTypes.string,
    item: PropTypes.object
};

// we need these props from the shipping quote request modal redux store (which is a parent of the
// NewPdpRoot Relay instance) in order to know if we were in the middle of the shipping request flow
// when the user was prompted to login, triggering a re-render of this component.
const connectToRedux = connect(({ isSubmitAfterAuth, openModalKey }) =>
    ({ isSubmitAfterAuth, openModalKey }));

module.exports = Relay.createContainer(connectToRedux(provideContext(connectToStores(
    ShippingDetailsLink,
    [
        ItemStore,
        UserStore
    ],
    context => {
        const userStore = context.getStore(UserStore);
        const itemStore = context.getStore(ItemStore);
        return ({
            currency: userStore.getCurrency(),
            copy: itemStore.getCopy()
        });
    }
))), {
    initialVariables: {
        userId: null,
        itemId: null
    },
    fragments: {
        user: ({itemId, userId}) => Relay.QL`
            fragment on User {
                canRequestShippingQuote: hasPermission(name: "B_REQUEST_SHIPPING_QUOTE")
                ${ShippingDetailsModal.getFragment('user', {itemId, userId})}
                ${RequestShippingModal.getFragment('user', {itemId, userId})}
            }
        `,
        item: () => Relay.QL`
            fragment on Item {
                ${ShippingDetailsModal.getFragment('item')}
            }
        `,
        viewer: ({itemId, userId}) => Relay.QL`
            fragment on Viewer {
                ${ShippingDetailsModal.getFragment('viewer', {itemId, userId})}
                ${RequestShippingModal.getFragment('viewer', {itemId, userId})}
            }
        `
    }
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Shipping/ShippingDetailsLink.jsx